import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import Modal from 'modules/views/shared/Modal';
import TextField from 'modules/views/shared/TextField';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { IContact } from 'modules/state-mgmt/workerData';
import { STYLE } from 'constants/style';

interface IEnterPasswordModalProps {
  isVisible: boolean;
  onClose: () => void;
  contact: IContact | null;
  action: 'makePrimary' | 'verify' | 'add';
  addContactValue?: string;
  isLoading: boolean;
  error: string;
  isAdmin: boolean;
  onSubmit: (password: string) => void;
}

export const EnterPasswordModal: React.FC<IEnterPasswordModalProps> = ({
  isVisible,
  onClose,
  contact,
  action,
  addContactValue,
  isLoading,
  error,
  isAdmin,
  onSubmit,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!isVisible) {
      setPassword('');
    }
  }, [isVisible]);

  const handleSubmit = () => {
    onSubmit(password);
  };
  const submitOnEnter = useSubmitOnEnter(handleSubmit, !password || isLoading);

  const contactLabel =
    contact?.contactType === 0
      ? t('myProfile.loginInformation.enterPasswordModal.emailLabel')
      : t('myProfile.loginInformation.enterPasswordModal.phoneLabel');
  const contactTypeLabel =
    contact?.contactType === 0
      ? t('myProfile.loginInformation.enterPasswordModal.emailTypeLabel')
      : t('myProfile.loginInformation.enterPasswordModal.phoneTypeLabel');

  const content = (
    <Box className={classes.content}>
      {action === 'add' && (
        <Typography className={classes.description}>
          {t('myProfile.loginInformation.enterPasswordModal.addDescriptionPrefix')}{' '}
          <span className={classes.contactValue}>{addContactValue}</span>{' '}
          {t('myProfile.loginInformation.enterPasswordModal.addDescriptionSuffix')}
        </Typography>
      )}
      {action === 'makePrimary' && (
        <>
          <Typography className={classes.description}>
            {t('myProfile.loginInformation.enterPasswordModal.makePrimaryContactPrefix')}{' '}
            <span className={classes.contactValue}>{contact?.contactValue}</span>{' '}
            {t('myProfile.loginInformation.enterPasswordModal.makePrimaryContactSuffix', { contactLabel })}
          </Typography>
          <Typography className={classes.description}>
            {t('myProfile.loginInformation.enterPasswordModal.makePrimaryInfo', { contactTypeLabel })}
          </Typography>
          <Typography className={classes.description}>
            {t('myProfile.loginInformation.enterPasswordModal.makePrimaryWarning', { contactTypeLabel })}
          </Typography>
        </>
      )}
      {action === 'verify' && (
        <Typography className={classes.description}>
          {t('myProfile.loginInformation.enterPasswordModal.verifyDescriptionPrefix')}{' '}
          <span className={classes.contactValue}>{contact?.contactValue}</span>.{' '}
          {t('myProfile.loginInformation.enterPasswordModal.verifyDescriptionSuffix')}
        </Typography>
      )}
      <TextField
        type={isAdmin ? 'text' : 'password'}
        maskText={isAdmin}
        placeholder={t('myProfile.loginInformation.enterPasswordModal.passwordPlaceholder')}
        value={password}
        testId="enter-password-input"
        onChange={e => setPassword(e.target.value)}
        onKeyDown={submitOnEnter}
        fullWidth
        autoComplete={isAdmin ? 'off' : 'current-password'}
      />
      {!!error && (
        <Typography className={classes.errorText} data-testid="enter-password-error">
          {error}
        </Typography>
      )}
    </Box>
  );

  return (
    <Modal
      title={t('myProfile.loginInformation.enterPasswordModal.title')}
      isVisible={isVisible}
      Content={content}
      testId="enter-password-modal"
      disableBackdropClose={isLoading}
      actionButtons={[
        {
          label: t('myProfile.loginInformation.enterPasswordModal.submit'),
          testId: 'enter-password-submit-button',
          onClick: handleSubmit,
          isLoading,
          disabled: !password || isLoading,
        },
      ]}
      onClose={onClose}
    />
  );
};

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '420px',
    '@media (max-width: 600px)': {
      minWidth: 'unset',
    },
  },
  description: {
    fontFamily: STYLE.FONT.PRIMARY_FONT,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    color: STYLE.COLOR.TEXT1,
  },
  contactValue: {
    color: STYLE.COLOR.ACCENT_PRIMARY,
    fontWeight: 600,
  },
  errorText: {
    fontFamily: STYLE.FONT.PRIMARY_FONT,
    fontSize: '12px',
    color: STYLE.COLOR.ERROR,
    marginTop: '-8px',
  },
}));
